import { defineComponent as _defineComponent } from 'vue'
import { resolveComponent as _resolveComponent, createVNode as _createVNode, unref as _unref, withCtx as _withCtx, renderList as _renderList, Fragment as _Fragment, openBlock as _openBlock, createElementBlock as _createElementBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode, createElementVNode as _createElementVNode, mergeProps as _mergeProps } from "vue"
import _imports_0 from '/images/dash_preview.webp'


const _hoisted_1 = { class: "flex justify-center items-center" }
const _hoisted_2 = { class: "mt-20" }
const _hoisted_3 = { class: "mt-20" }
const _hoisted_4 = { class: "mt-20" }
const _hoisted_5 = { class: "flex flex-col md:flex-row gap-8 w-full mt-8" }

import { Motion } from "motion-v";

interface PageCard {
  title: string;
  description: string;
  icon: string;
  variant?: "soft" | "solid" | "outline" | "subtle" | "ghost" | "naked";
  spotlight?: boolean;
  class?: string;
  orientation?: "vertical" | "horizontal";
  lottie?: {
    path: string;
    width: number;
    height: number;
  };
}


export default /*@__PURE__*/_defineComponent({
  __name: 'centralised-dashboard',
  setup(__props) {

const _colorMode = useColorMode();

defineOgImage("MainOg", {
  title: "Centralised Dashboard - Your Organisational Command Centre",
  description:
    "Gain a real-time, unified view of your entire operation. Monitor every centre's activity, compliance, and care delivery from a single, intuitive screen.",
  theme: "#bc9fff",
  colorMode: "light",
});

useSeoMeta({
  title: "Centralised Dashboard - Your Organisational Command Centre | FamilyAxis",
  description:
    "Gain a real-time, unified view of your entire organisation. Monitor every centre's activity, compliance, and care delivery from a single, intuitive screen designed for multi-site residential family centres.",
  keywords:
    "family assessment, family assessment centres, centralised dashboard, operations management, care home dashboard, unified interface, monitoring, reporting, admin panel, multi-site management, real-time oversight",
  author: "FamilyAxis",
  ogTitle: "Centralised Dashboard - Your Organisational Command Centre | FamilyAxis",
  ogDescription:
    "Gain a real-time, unified view of your entire operation. Monitor every centre's activity, compliance, and care delivery from a single, intuitive screen.",
  ogUrl: "https://familyaxis.com/more/centralised-dashboard",
  ogType: "website",
  ogSiteName: "FamilyAxis",
  twitterCard: "summary_large_image",
  twitterTitle: "Centralised Dashboard - Your Organisational Command Centre | FamilyAxis",
  twitterDescription:
    "Gain a real-time, unified view of your entire operation. Monitor every centre's activity, compliance, and care delivery.",
  twitterSite: "@familyaxis",
  robots: "index, follow",
});

useHead({
  link: [
    {
      rel: "canonical",
      href: "https://familyaxis.com/more/centralised-dashboard",
    },
  ],
});

const multiSiteCards = ref<PageCard[]>([
  {
    title: "Your Centre at a Glance",
    description:
      "Get an immediate snapshot of your chosen centre's recent activity, including recently added residents, latest notifications, completed tasks, and safeguarding incidents.",
    icon: "i-heroicons-chart-bar",
    class: "lg:row-span-2",
    orientation: "vertical" as const,
    lottie: "warning_message",
  },
  {
    title: "Instant Centre Switching",
    description:
      "Use the dropdown at the top of your dashboard to seamlessly toggle between different centres. Every widget updates automatically to show data relevant to your selected home.",
    icon: "i-heroicons-arrow-path",
    variant: "soft" as const,
  },
  {
    title: "Real-Time Monitoring",
    description:
      "Monitor the real-time pulse of any centre in your organisation with live updates and instant notifications for critical events.",
    icon: "i-heroicons-signal",
    class: "lg:row-span-2",
    orientation: "vertical" as const,
    lottie: "timer",
    reverse: true,
  },
  {
    title: "Compliance Tracking",
    description:
      "Track all safeguarding incidents logged in the last 7 days and monitor compliance across all your centres from one central location.",
    icon: "i-heroicons-shield-check",
    variant: "soft" as const,
  },
]);

const navigationCards = ref<PageCard[]>([
  {
    title: "All-in-One Navigation",
    description:
      "The left-hand menu provides quick access to every essential area: detailed resident profiles, direct work logs, task management, and manager settings.",
    icon: "i-heroicons-bars-3",
    variant: "soft" as const,
  },
  {
    title: "Universal Search",
    description:
      "Find specific residents, staff, or documents across your entire platform instantly with the integrated search bar.",
    icon: "i-heroicons-magnifying-glass",
    variant: "soft" as const,
  },
  {
    title: "Visual Data at a Glance",
    description:
      "Understand your centre's workload and priorities through live charts showing task status and priority distribution for informed decision-making.",
    icon: "i-heroicons-eye",
    variant: "soft" as const,
  },
]);

const overviewFeatures = ref<PageCard[]>([
  {
    title: "Recently Added Residents",
    description:
      "Stay updated with the latest parents joining your centres and track their onboarding progress.",
    icon: "i-heroicons-user-plus",
  },
  {
    title: "Latest Notifications",
    description:
      "Never miss important updates with real-time notifications about critical events and system alerts.",
    icon: "i-heroicons-bell",
    variant: "soft" as const,
  },
  {
    title: "Task Completion Status",
    description:
      "Monitor completed tasks and those still due across all your centres with clear visual indicators.",
    icon: "i-heroicons-check-circle",
  },
  {
    title: "Safeguarding Incidents",
    description:
      "Track all safeguarding incidents logged in the last 7 days with detailed reporting and follow-up capabilities.",
    icon: "i-heroicons-exclamation-triangle",
    variant: "soft" as const,
  },
  {
    title: "Recent Observations",
    description: "Access the most recent observations with detailed insights.",
    icon: "i-heroicons-document-text",
  },
  {
    title: "Multi-Site Overview",
    description:
      "Compare performance and activity across multiple centres to identify trends and best practices.",
    icon: "i-heroicons-building-office-2",
    variant: "soft" as const,
  },
]);

return (_ctx: any,_cache: any) => {
  const _component_UPageHeader = _resolveComponent("UPageHeader")!
  const _component_UPageSection = _resolveComponent("UPageSection")!
  const _component_LottieWrapper = _resolveComponent("LottieWrapper")!
  const _component_UPageCard = _resolveComponent("UPageCard")!
  const _component_UPageGrid = _resolveComponent("UPageGrid")!
  const _component_UPageHero = _resolveComponent("UPageHero")!
  const _component_LazyShowCaseCard = _resolveComponent("LazyShowCaseCard")!
  const _component_LazyStartSignup = _resolveComponent("LazyStartSignup")!
  const _component_UPageBody = _resolveComponent("UPageBody")!
  const _component_UPage = _resolveComponent("UPage")!
  const _component_UContainer = _resolveComponent("UContainer")!

  return (_openBlock(), _createBlock(_component_UContainer, null, {
    default: _withCtx(() => [
      _createVNode(_component_UPage, { class: "pt-10" }, {
        default: _withCtx(() => [
          _createVNode(_component_UPageHeader, { description: "Gain a real-time, unified view of your entire organisation. Monitor every centre's activity, compliance, and care delivery from a single, intuitive screen designed for multi-site residential family centres." }),
          _createVNode(_component_UPageBody, null, {
            default: _withCtx(() => [
              _createVNode(_unref(Motion), {
                as: "div",
                initial: { opacity: 0, y: 20 },
                "while-in-view": { opacity: 1, y: 0 },
                viewport: { once: true },
                transition: { duration: 0.4, delay: 0.2 },
                class: "mt-20"
              }, {
                default: _withCtx(() => [
                  _createVNode(_component_UPageSection, {
                    title: "Live Overview & Multi-Site Management",
                    orientation: "vertical",
                    description: "Effortlessly monitor the real-time pulse of any centre in your organisation with our powerful centralised dashboard.",
                    ui: {
              container: 'flex flex-col lg:grid py-16 sm:py-24 lg:py-5 gap-8 sm:gap-16',
            }
                  })
                ]),
                _: 1
              }),
              _createVNode(_component_UPageGrid, null, {
                default: _withCtx(() => [
                  (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(_unref(multiSiteCards), (card, index) => {
                    return (_openBlock(), _createBlock(_component_UPageCard, _mergeProps({ key: index }, { ref_for: true }, card, {
                      "data-aos": `fade-up`,
                      "data-aos-delay": (index + 1) * 100,
                      spotlight: !!card.spotlight
                    }), {
                      default: _withCtx(() => [
                        _createElementVNode("div", _hoisted_1, [
                          (card.lottie)
                            ? (_openBlock(), _createBlock(_component_LottieWrapper, {
                                key: 0,
                                "animation-link": `/animations/${card.lottie}.json`,
                                "no-margin": true,
                                loop: true,
                                speed: 1.0,
                                scale: 1.0
                              }, null, 8, ["animation-link"]))
                            : _createCommentVNode("", true)
                        ])
                      ]),
                      _: 2
                    }, 1040, ["data-aos-delay", "spotlight"]))
                  }), 128))
                ]),
                _: 1
              }),
              _createElementVNode("div", _hoisted_2, [
                _createVNode(_component_UPageHero, {
                  title: "Centre Snapshot",
                  description: "The main dashboard provides an immediate snapshot of your chosen centre's recent activity, keeping you informed of everything that matters.",
                  ui: {
              container: 'py-8 sm:py-6 lg:py-6',
              title: 'text-4xl sm:text-4xl text-pretty tracking-tight font-bold',
            }
                }),
                _createVNode(_component_UPageGrid, { class: "mt-12" }, {
                  default: _withCtx(() => [
                    (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(_unref(overviewFeatures), (card, index) => {
                      return (_openBlock(), _createBlock(_unref(Motion), {
                        key: index,
                        as: "div",
                        initial: { opacity: 0, y: 20 },
                        "while-in-view": { opacity: 1, y: 0 },
                        viewport: { once: true },
                        transition: { duration: 0.4, delay: (index + 1) * 0.1 }
                      }, {
                        default: _withCtx(() => [
                          _createVNode(_component_UPageCard, _mergeProps({ ref_for: true }, card), null, 16)
                        ]),
                        _: 2
                      }, 1032, ["transition"]))
                    }), 128))
                  ]),
                  _: 1
                })
              ]),
              _createVNode(_unref(Motion), {
                as: "div",
                initial: { opacity: 0, y: 20 },
                "while-in-view": { opacity: 1, y: 0 },
                viewport: { once: true },
                transition: { duration: 0.4, delay: 0.3 },
                class: "mt-20"
              }, {
                default: _withCtx(() => [
                  _createVNode(_component_UPageSection, {
                    title: "Quick Navigation & Data Insights",
                    orientation: "horizontal",
                    description: "Find what you need and understand your data at a glance with built-in tools designed for efficiency and informed decision-making.",
                    align: "left",
                    ui: {
              container: 'flex flex-col lg:grid py-16 sm:py-24 lg:py-5 gap-8 sm:gap-16',
            }
                  }, {
                    default: _withCtx(() => [...(_cache[0] || (_cache[0] = [
                      _createElementVNode("div", { class: "relative" }, [
                        _createElementVNode("img", {
                          src: _imports_0,
                          alt: "Dashboard Navigation and Data Insights",
                          class: "w-full h-auto rounded-lg",
                          loading: "lazy"
                        })
                      ], -1)
                    ]))]),
                    _: 1
                  })
                ]),
                _: 1
              }),
              _createElementVNode("div", _hoisted_3, [
                _createVNode(_component_UPageHero, {
                  title: "Built for Efficiency",
                  description: "Every tool is designed to help you find what you need quickly and make informed decisions with confidence.",
                  ui: {
              container: 'py-8 sm:py-6 lg:py-6',
              title: 'text-4xl sm:text-4xl text-pretty tracking-tight font-bold',
            }
                }),
                _createVNode(_component_UPageGrid, { class: "mt-12" }, {
                  default: _withCtx(() => [
                    (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(_unref(navigationCards), (card, index) => {
                      return (_openBlock(), _createBlock(_unref(Motion), {
                        key: index,
                        as: "div",
                        initial: { opacity: 0, y: 20 },
                        "while-in-view": { opacity: 1, y: 0 },
                        viewport: { once: true },
                        transition: { duration: 0.4, delay: (index + 1) * 0.1 }
                      }, {
                        default: _withCtx(() => [
                          _createVNode(_component_UPageCard, _mergeProps({ ref_for: true }, card), null, 16)
                        ]),
                        _: 2
                      }, 1032, ["transition"]))
                    }), 128))
                  ]),
                  _: 1
                })
              ]),
              _createElementVNode("div", _hoisted_4, [
                _createVNode(_component_UPageHero, {
                  title: "Continue Your Journey",
                  description: "Explore more tools that integrate with the Centralised Dashboard, enabling you to run your centre with ease.",
                  ui: {
              container: 'py-8 sm:py-6 lg:py-6',
              title: 'text-4xl sm:text-4xl text-pretty tracking-tight font-bold',
            }
                }),
                _createElementVNode("div", _hoisted_5, [
                  _createVNode(_component_LazyShowCaseCard, {
                    title: "Resident Profiles",
                    description: "A dedicated space for each resident, tracking their journey and needs.",
                    icon: "i-heroicons-user-circle",
                    image: "/howitworks/activate_resident.webp",
                    "image-position": "bottom",
                    link: "/more/resident-profiles",
                    class: "w-full md:w-4/7 h-110"
                  }),
                  _createVNode(_component_LazyShowCaseCard, {
                    title: "Family Engagement",
                    description: "Tools for life story work and parent profiles to keep families involved.",
                    icon: "i-heroicons-users",
                    image: "/images/about/care1.jpg",
                    "image-position": "cover",
                    link: "/more/family-engagement",
                    class: "w-full md:w-3/7 h-110"
                  })
                ])
              ]),
              _createVNode(_component_LazyStartSignup)
            ]),
            _: 1
          })
        ]),
        _: 1
      })
    ]),
    _: 1
  }))
}
}

})